/**
 * AskProviderQuestionModal
 *
 * Modal for submitting a question to the Clinical Quality team about a specific
 * provider. Offers a dynamic multi-select of relevant topics (Facility Assignments
 * + the measures active for this provider's specialty and quarter), followed by a
 * free-text question field.
 *
 * Topics are driven by `och.equip_config_measure_schedule` via
 * `getActiveMeasureNames`, so they automatically reflect what is relevant for
 * the clinician type viewing the page. The `specialty` prop arrives as the
 * canonical DB `bonus_eligible_type` (e.g. "PCP", "PC Physician") — no
 * leadership display-label normalization is needed here.
 *
 * Rendered at the ProgressDetailsCore level (not nested inside another Dialog)
 * to avoid shadcn Dialog nesting conflicts.
 */

import { useState, useMemo } from 'react'
import { Loader2, ChevronDown, X } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { askQuestion } from '@/services/askQuestionService'
import type { QuestionContext, MeasureScheduleItem } from '@/lib/equip-types'
import {
  MEASURE_DISPLAY_NAMES,
  type MeasureName,
} from '@/lib/equip-measure-config'
import {
  getActiveMeasureNames,
  LEADERSHIP_MEASURE_ORDER,
} from '@/lib/measureScheduleSelectors'
import { useMeasureThresholdMap } from '@/hooks/equip/useMeasureThresholdMap'

interface AskProviderQuestionModalProps {
  open: boolean
  onClose: () => void
  providerName: string
  providerEmail: string
  /** Provider's bonusEligibleType (e.g. "PC Physician", "PCP", "Teletriage") */
  specialty: string | null
  year: number
  quarter: number
}

/** Always offered for every clinician type */
const UNIVERSAL_TOPICS = ['Facility Assignments']

/**
 * Build the ordered list of topic options for the multi-select.
 * Facility Assignments comes first, then the measures active for this
 * bonus_eligible_type × quarter in leadership-column display order.
 *
 * When the schedule hasn't loaded yet (empty items), we return only the
 * universal topics so the dropdown stays usable without guessing at an
 * outdated hardcoded matrix.
 */
function getTopicOptions(
  schedule: MeasureScheduleItem[],
  specialty: string | null,
  quarter: number,
): string[] {
  const activeSet = getActiveMeasureNames(schedule, specialty, quarter)
  const ordered: string[] = activeSet
    ? LEADERSHIP_MEASURE_ORDER.filter(m => activeSet.has(m)).map(
        m => MEASURE_DISPLAY_NAMES[m as MeasureName] ?? m,
      )
    : []
  return [
    ...UNIVERSAL_TOPICS,
    ...ordered.filter((t) => !UNIVERSAL_TOPICS.includes(t)),
  ]
}

export function AskProviderQuestionModal({
  open,
  onClose,
  providerName,
  providerEmail,
  specialty,
  year,
  quarter,
}: AskProviderQuestionModalProps) {
  const [selectedTopics, setSelectedTopics] = useState<string[]>([])
  const [questionText, setQuestionText] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [dropdownOpen, setDropdownOpen] = useState(false)

  const thresholdMap = useMeasureThresholdMap(year, quarter)
  const topicOptions = useMemo(
    () => getTopicOptions(thresholdMap.items, specialty, quarter),
    [thresholdMap.items, specialty, quarter]
  )

  const toggleTopic = (topic: string) => {
    setSelectedTopics((prev) =>
      prev.includes(topic) ? prev.filter((t) => t !== topic) : [...prev, topic]
    )
  }

  const removeTopic = (topic: string) => {
    setSelectedTopics((prev) => prev.filter((t) => t !== topic))
  }

  const handleClose = () => {
    if (isSending) return
    setSelectedTopics([])
    setQuestionText('')
    setDropdownOpen(false)
    onClose()
  }

  const handleSend = async () => {
    if (!canSend) return
    setIsSending(true)

    const context: QuestionContext = {
      type: 'provider',
      identifier: providerName,
      providerName,
      providerEmail,
      year,
      quarter,
      additionalContext: {
        topics: selectedTopics.join(', '),
      },
    }

    const success = await askQuestion(context, questionText)
    setIsSending(false)
    if (success) {
      setSelectedTopics([])
      setQuestionText('')
      onClose()
    }
  }

  const canSend = selectedTopics.length > 0 && questionText.trim().length > 0

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Ask a Question</DialogTitle>
          <DialogDescription>
            Select the topics your question relates to, then describe your
            question. It will be sent to the Clinical Quality team.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* ── Topic multi-select ── */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              Topic(s) <span className="text-destructive">*</span>
            </label>

            <Popover open={dropdownOpen} onOpenChange={setDropdownOpen}>
              <PopoverTrigger asChild>
                <button
                  className="w-full flex items-center justify-between px-3 py-2 text-sm border border-input rounded-md bg-background hover:bg-accent/50 transition-colors text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  aria-expanded={dropdownOpen}
                  disabled={isSending}
                >
                  <span
                    className={
                      selectedTopics.length === 0
                        ? 'text-muted-foreground'
                        : 'text-foreground'
                    }
                  >
                    {selectedTopics.length === 0
                      ? 'Select topics...'
                      : `${selectedTopics.length} topic${selectedTopics.length > 1 ? 's' : ''} selected`}
                  </span>
                  <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                </button>
              </PopoverTrigger>

              <PopoverContent
                className="w-[var(--radix-popover-trigger-width)] p-1"
                align="start"
              >
                <div className="max-h-52 overflow-y-auto">
                  {topicOptions.map((topic) => {
                    const isSelected = selectedTopics.includes(topic)
                    return (
                      <button
                        key={topic}
                        onClick={() => toggleTopic(topic)}
                        className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm rounded-sm transition-colors text-left ${
                          isSelected
                            ? 'bg-primary/10 text-primary'
                            : 'hover:bg-accent text-foreground'
                        }`}
                      >
                        {/* Checkbox indicator */}
                        <div
                          className={`h-4 w-4 rounded border flex items-center justify-center shrink-0 transition-colors ${
                            isSelected
                              ? 'bg-primary border-primary'
                              : 'border-muted-foreground/40 bg-background'
                          }`}
                        >
                          {isSelected && (
                            <svg
                              className="h-2.5 w-2.5 text-primary-foreground"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={3}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                          )}
                        </div>
                        {topic}
                      </button>
                    )
                  })}
                </div>
              </PopoverContent>
            </Popover>

            {/* Selected topic chips */}
            {selectedTopics.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {selectedTopics.map((topic) => (
                  <Badge key={topic} variant="secondary" className="gap-1 pr-1 text-xs">
                    {topic}
                    <button
                      onClick={() => removeTopic(topic)}
                      className="ml-0.5 rounded-full hover:bg-muted-foreground/20 p-0.5 transition-colors"
                      disabled={isSending}
                      aria-label={`Remove ${topic}`}
                    >
                      <X className="h-2.5 w-2.5" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* ── Question text ── */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              Question <span className="text-destructive">*</span>
            </label>
            <Textarea
              placeholder="Type your question here..."
              value={questionText}
              onChange={(e) => setQuestionText(e.target.value)}
              rows={4}
              disabled={isSending}
              className="resize-none"
            />
          </div>

          {/* ── Email context summary ── */}
          <div className="rounded-md bg-muted/50 p-3 text-xs text-muted-foreground space-y-1">
            <span className="font-medium text-foreground text-xs block mb-1">
              Included in email:
            </span>
            <div>Provider: {providerName}</div>
            <div>
              Quarter: Q{quarter} {year}
            </div>
            <div>From: {providerEmail}</div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isSending}>
            Cancel
          </Button>
          <Button onClick={handleSend} disabled={!canSend || isSending}>
            {isSending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending...
              </>
            ) : (
              'Send Question'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
